import { MutableRefObject } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { round, roundToFixed } from '@oliasoft-open-source/units';
import autoTable, { FontStyle } from 'jspdf-autotable';
import i18n from 'i18next';
import translations from 'src/internationalisation/translation-map.json';
import { getMean } from 'views/simulations/result-overview/result-utils';
import { Operation } from 'client/common/interfaces/activity-model.ts';
import { lineInterval } from 'views/report/utils/style-consts.ts';
import { addTitle } from 'views/report/utils/add-title/add-title.ts';

interface TableRow {
  name: string;
  certainty: number;
  mean: number;
}

export const addRisksPage = async (
  doc: jsPDF,
  padding: number,
  forecastLogoHeight: number,
  chartRef: MutableRefObject<null>,
  riskFilter: any[],
  operations: Operation[],
): Promise<void> => {
  doc.addPage();

  addTitle(doc, 'Risks', {
    x: padding,
    y: forecastLogoHeight + padding + lineInterval,
  });

  if (!chartRef.current) {
    console.error('The component is not mounted yet.');
    return;
  }
  const element = chartRef.current;
  const canvas = await html2canvas(element, { scale: 3 });
  const resizedCanvas = document.createElement('canvas');
  const ctx = resizedCanvas.getContext('2d');
  resizedCanvas.width = canvas.width * 0.8;
  resizedCanvas.height = canvas.height * 0.8;
  ctx?.drawImage(canvas, 0, 0, resizedCanvas.width, resizedCanvas.height);

  const imgData = resizedCanvas.toDataURL('image/jpeg', 0.8);
  const pdfWidth = doc.internal.pageSize.getWidth();
  const chartWidth = pdfWidth - 2 * padding;
  const chartHeight = (resizedCanvas.height * chartWidth) / resizedCanvas.width;

  doc.addImage(imgData, 'JPEG', padding, 35, chartWidth, chartHeight);
  const filterMap = riskFilter.reduce((acc, curr) => ({ ...acc, [curr.value]: curr.selected }), {});

  const tableData: TableRow[] = operations
    .filter((operation) => filterMap[operation.operationId] && filterMap[operation.projectSectionId])
    .reduce((riskData, operation) => {
      const operationTaskData = operation.tasks
        .filter((task) => !task.isBranch && task.parentId !== null)
        .map((task) => ({
          name: task.name,
          certainty: task.certainty,
          mean: getMean(task, operation.tasks),
        }));
      return [...riskData, ...operationTaskData];
    }, [] as TableRow[]);
  const rows = tableData?.map((row) => [
    { content: row.name, styles: { fontStyle: 'bold' as FontStyle } },
    round(row.certainty),
    roundToFixed(row.mean, 2),
  ]);

  const tableStartY = padding + forecastLogoHeight + chartHeight + 2 * lineInterval;
  autoTable(doc, {
    head: [
      [
        i18n.t(translations.risks_risks),
        i18n.t(translations.simulations_probabilityOfOccurring),
        i18n.t(translations.simulations_meanTime),
      ].map((label) => ({ content: label, styles: { fontStyle: 'bold' } })),
      ['', i18n.t(translations.percent), i18n.t(translations.hours)].map((content) => ({
        content,
        styles: { fontStyle: 'italic' },
      })),
    ],
    body: rows,
    startY: tableStartY,
    theme: 'plain',
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 'auto' },
    },
    styles: {
      fontSize: 10,
      cellPadding: { top: 2, right: 2, bottom: 2, left: 0 },
      halign: 'left',
    },

    didDrawCell: (data) => {
      const { cell, doc } = data;
      const { x, y, width, height } = cell;
      // Draw bottom border for each cell
      doc.setDrawColor('#90908d');
      doc.setLineWidth(0.2);
      doc.line(x, y + height, x + width, y + height);
    },
    margin: { top: 30 },
  });
};
